/**
 * Global error reporting utility.
 * Currently logs to console (captured by Netlify).
 * When Sentry DSN is configured, will forward to Sentry.
 */

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || '';

interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  extra?: Record<string, unknown>;
}

export function reportError(error: Error | unknown, context?: ErrorContext): void {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  
  // Always log locally
  console.error('[MM Error]', {
    message: errorObj.message,
    stack: errorObj.stack,
    ...context,
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : 'server',
  });

  // If Sentry is configured, forward there
  if (SENTRY_DSN && typeof window !== 'undefined') {
    // Lightweight Sentry transport (no SDK needed)
    try {
      const payload = {
        exception: {
          values: [{
            type: errorObj.name,
            value: errorObj.message,
            stacktrace: errorObj.stack ? { frames: parseStack(errorObj.stack) } : undefined,
          }],
        },
        tags: context?.component ? { component: context.component } : undefined,
        extra: context?.extra,
        request: { url: window.location.href },
        timestamp: Date.now() / 1000,
      };
      
      const [, key, projectId] = SENTRY_DSN.match(/https:\/\/([^@]+)@[^/]+\/(\d+)/) || [];
      if (key && projectId) {
        fetch(`https://sentry.io/api/${projectId}/store/?sentry_key=${key}&sentry_version=7`, {
          method: 'POST',
          body: JSON.stringify(payload),
        }).catch(() => {}); // Fire and forget
      }
    } catch {
      // Don't let error reporting cause errors
    }
  }
}

function parseStack(stack: string): Array<{filename: string; function: string; lineno: number}> {
  return stack.split('\n').slice(1, 6).map(line => {
    const match = line.match(/at\s+(.+?)\s+\((.+?):(\d+):\d+\)/);
    return match ? { function: match[1], filename: match[2], lineno: parseInt(match[3]) } : { function: line.trim(), filename: 'unknown', lineno: 0 };
  });
}

// Auto-install global handlers
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    reportError(event.error || event.message, { action: 'window.onerror' });
  });
  
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { action: 'unhandledrejection' });
  });
}
